import { KeyboardEvent, useCallback, useState } from 'react'

import { KEY_VALUES } from '../constants'
import { PickerItem } from '../types'

const FOCUSABLE_ITEMS_QUERY = '[data-key][aria-disabled="false"]'

export type FocusItemValue<Item extends PickerItem> = Item['value'] | null | undefined

interface UseFocusItemValueParams<Item extends PickerItem> {
  defaultFocusItemValue: FocusItemValue<Item>
  data: Item[]
  getMenu: () => HTMLElement | null
}

interface MoveFocusParams<Item extends PickerItem> {
  items: (Item | undefined)[]
  index: number
}

const getNextIndex = <Item extends PickerItem>({ items, index }: MoveFocusParams<Item>) =>
  index + 2 > items.length ? 0 : index + 1

const getPreviousIndex = <Item extends PickerItem>({ items, index }: MoveFocusParams<Item>) =>
  index === 0 ? items.length - 1 : index - 1

export const useFocusItemValue = <Item extends PickerItem>({
  defaultFocusItemValue,
  data,
  getMenu,
}: UseFocusItemValueParams<Item>) => {
  const [focusItemValue, setFocusItemValue] = useState<FocusItemValue<Item>>(defaultFocusItemValue)
  const [keys, setKeys] = useState<(string | undefined)[]>([])

  const getFocusableMenuItems = useCallback(() => {
    let currentKeys = keys
    if (currentKeys.length === 0) {
      const focusableElements = getMenu()?.querySelectorAll<HTMLElement>(FOCUSABLE_ITEMS_QUERY)
      currentKeys = Array.from(focusableElements ?? []).map(element => element.dataset.key)
      setKeys(currentKeys)
    }

    return currentKeys.map(key => data.find(item => `${item.value}` === key))
  }, [data, getMenu, keys])

  const moveFocus = useCallback(
    (getIndex: (params: MoveFocusParams<Item>) => number) => {
      const items = getFocusableMenuItems()
      const index = items.findIndex(item => Object.is(focusItemValue, item?.value))
      const focusItem = items[getIndex({ items, index })]

      if (focusItem !== undefined) {
        setFocusItemValue(focusItem.value)
      }
    },
    [focusItemValue, getFocusableMenuItems],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      switch (event.key) {
        case KEY_VALUES.DOWN:
          moveFocus(getNextIndex)
          event.preventDefault()
          break
        case KEY_VALUES.UP:
          moveFocus(getPreviousIndex)
          event.preventDefault()
          break
        case KEY_VALUES.ENTER:
          event.preventDefault()
          break
      }
    },
    [moveFocus],
  )

  return { focusItemValue, setFocusItemValue, onKeyDown: handleKeyDown }
}
